import { Box, Container, Heading, Text, VStack, Flex, Stack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Building2, UserCheck } from 'lucide-react'
import { PropertyForm } from '@/features/properties/components/PropertyForm'
import { useCreateProperty } from '@/features/properties/hooks/useProperties'
import { useAgency } from '@/features/agencies/hooks/useAgency'
import type { PropertyFormData } from '@/features/properties/validation'
import { useAuth, useLogout } from '../hooks/useAuth'
import { useQueryClient } from '@tanstack/react-query'

export function OnboardingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const createProperty = useCreateProperty()
  const logout = useLogout()
  const { data: agency } = useAgency()
  const { data: auth } = useAuth()

  const handleSubmit = async (data: PropertyFormData) => {
    if (!agency?.id) {
      console.error('Agency ID not found')
      return
    }

    if (!auth?.user?.id) {
      console.error('User ID not found')
      return
    }

    try {
      // Create the property with the agency_id
      await createProperty.mutateAsync({
        ...data,
        agency_id: agency.id,
      })

      // Wait for auth queries to refetch and get the new residence
      // This ensures the ProtectedIntranetRoute sees the updated residence list
      await queryClient.refetchQueries({ queryKey: ['auth'] })

      // Navigate to dashboard after successful creation
      navigate('/app/dashboard')
    } catch (error) {
      console.error('Error creating property:', error)
    }
  }

  const handleSkip = async () => {
    // CRITICAL: If user has pending invitation, they cannot skip onboarding
    // They must either create their first residence or logout
    // This prevents a redirect loop: onboarding -> dashboard -> onboarding
    if (auth?.profile?.invitation_status === 'pending') {
      // Logout the user and redirect to login
      await logout.mutateAsync()
      navigate('/app/login')
    } else {
      // For normal users (not invited), allow skip to dashboard
      navigate('/app/dashboard')
    }
  }

  return (
    <Box minH="100vh" bg="gray.50" py={12}>
      <Container maxW="container.md">
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Flex justify="center" mb={4}>
              <img
                src="/assets/logo-compact-light.svg"
                alt="Kopro"
                style={{ height: '60px', width: 'auto' }}
              />
            </Flex>
            <Heading size="2xl" mb={3} color="gray.900">
              Bienvenue sur Kopro ! 🎉
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="600px" mx="auto">
              Félicitations ! Votre compte et votre agence sont créés.
              <br />
              Créons maintenant votre première résidence pour commencer.
            </Text>
          </Box>

          {/* Progress Steps */}
          <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
            <Stack direction="row" justify="space-between" align="center" gap={4}>
              <Flex direction="column" align="center" flex={1}>
                <Box
                  bg="brand.800"
                  color="white"
                  borderRadius="full"
                  p={3}
                  mb={2}
                  boxShadow="md"
                >
                  <CheckCircle2 size={24} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  Compte créé
                </Text>
              </Flex>

              <Box h="2px" flex={1} bg="brand.800" />

              <Flex direction="column" align="center" flex={1}>
                <Box
                  bg="brand.800"
                  color="white"
                  borderRadius="full"
                  p={3}
                  mb={2}
                  boxShadow="md"
                >
                  <UserCheck size={24} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  Agence créée
                </Text>
              </Flex>

              <Box h="2px" flex={1} bg="gray.300" />

              <Flex direction="column" align="center" flex={1}>
                <Box
                  bg="white"
                  color="brand.800"
                  borderRadius="full"
                  p={3}
                  mb={2}
                  border="2px solid"
                  borderColor="brand.800"
                >
                  <Building2 size={24} />
                </Box>
                <Text fontSize="sm" fontWeight="medium" color="brand.800">
                  Première résidence
                </Text>
              </Flex>
            </Stack>
          </Box>

          {/* Main Card */}
          <Box bg="white" p={8} borderRadius="xl" boxShadow="lg">
            <Box mb={6}>
              <Heading size="lg" color="gray.900" mb={2}>
                Créer votre première résidence
              </Heading>
              <Text color="gray.600">
                Remplissez les informations de base de votre première copropriété. Vous pourrez
                ajouter plus de détails plus tard.
              </Text>
            </Box>

            <PropertyForm
              onSubmit={handleSubmit}
              onCancel={handleSkip}
              isLoading={createProperty.isPending}
              showActions={true}
              submitLabel="Créer ma première résidence"
              cancelLabel="Je le ferai plus tard"
            />
          </Box>

          {/* Help Text */}
          <Box textAlign="center">
            <Text fontSize="sm" color="gray.500">
              💡 Vous pourrez ajouter d'autres résidences plus tard depuis le tableau de bord
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
